import { API_BASE_URL } from '../config/constants';

// Utilitaire de log côté frontend (console avec préfixe)
const log = (message) => {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] [TwitchAppService] ${message}`);
};

/**
 * Service pour gérer les applications Twitch
 */
class TwitchAppService {
  /**
   * Crée une nouvelle application Twitch
   * @param {Object} appData - Données de l'application
   * @param {string} appData.appName - Nom de l'application
   * @param {string} appData.clientId - Client ID Twitch
   * @param {string} appData.clientSecret - Client Secret Twitch
   * @param {string} appData.redirectUri - URI de redirection
   * @returns {Promise<Object>} L'application créée
   */
  static async createApp(appData) {
    log(`Création d'une nouvelle application Twitch: ${appData.appName}`);

    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/twitch/apps`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(appData),
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Construire un message d'erreur détaillé
      let errorMessage =
        errorData.error || "Erreur lors de la création de l'application";

      // Si des détails de validation sont présents, les ajouter
      if (
        errorData.details &&
        Array.isArray(errorData.details) &&
        errorData.details.length > 0
      ) {
        const validationErrors = errorData.details
          .map((detail) => detail.msg || detail.message)
          .filter((msg) => msg)
          .join(' ; ');

        if (validationErrors) {
          errorMessage = validationErrors;
        }
      }

      log(`Erreur lors de la création de l'application: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    log(`Application créée avec succès: ${result.app?.appName || 'N/A'}`);
    return result.app;
  }

  /**
   * Récupère l'application Twitch de l'utilisateur connecté
   * @returns {Promise<Object|null>} L'application ou null si aucune
   */
  static async getUserApp() {
    log("Récupération de l'application Twitch de l'utilisateur");

    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/twitch/apps`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      log("Erreur lors de la récupération de l'application");
      throw new Error("Erreur lors de la récupération de l'application");
    }

    const data = await response.json();
    if (data.hasApp) {
      log(`Application trouvée: ${data.app?.appName || 'N/A'}`);
    } else {
      log('Aucune application trouvée pour cet utilisateur');
    }
    return data.hasApp ? data.app : null;
  }

  /**
   * Met à jour l'application Twitch
   * @param {Object} appData - Données mises à jour
   * @returns {Promise<Object>} L'application mise à jour
   */
  static async updateApp(appData) {
    log(`Mise à jour de l'application Twitch: ${appData.appName || 'N/A'}`);

    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/twitch/apps`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(appData),
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Construire un message d'erreur détaillé
      let errorMessage =
        errorData.error || "Erreur lors de la mise à jour de l'application";

      // Si des détails de validation sont présents, les ajouter
      if (
        errorData.details &&
        Array.isArray(errorData.details) &&
        errorData.details.length > 0
      ) {
        const validationErrors = errorData.details
          .map((detail) => detail.msg || detail.message)
          .filter((msg) => msg)
          .join(' ; ');

        if (validationErrors) {
          errorMessage = validationErrors;
        }
      }

      log(`Erreur lors de la mise à jour de l'application: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    log(`Application mise à jour avec succès: ${result.app?.appName || 'N/A'}`);
    return result.app;
  }

  /**
   * Récupère l'URI de redirection suggérée basée sur l'environnement actuel
   * @returns {Promise<string>} L'URI de redirection suggérée
   */
  static async getSuggestedRedirectUri() {
    log("Récupération de l'URI de redirection suggérée");

    const response = await fetch(
      `${API_BASE_URL}/twitch/suggested-redirect-uri`,
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      log("Erreur lors de la récupération de l'URI suggérée");
      throw new Error("Erreur lors de la récupération de l'URI suggérée");
    }

    const data = await response.json();
    log(`URI suggérée récupérée: ${data.suggestedRedirectUri}`);
    return data.suggestedRedirectUri;
  }

  /**
   * Supprime l'application Twitch
   * @returns {Promise<void>}
   */
  static async deleteApp() {
    log("Suppression de l'application Twitch");

    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/twitch/apps`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage =
        errorData.error || "Erreur lors de la suppression de l'application";
      log(`Erreur lors de la suppression de l'application: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    log('Application supprimée avec succès');
  }

  /**
   * Active/désactive l'application Twitch
   * @param {boolean} isActive - État souhaité
   * @returns {Promise<Object>} L'application mise à jour
   */
  static async toggleApp(isActive) {
    log(`Changement de statut de l'application: isActive=${isActive}`);

    // Le token JWT est dans un cookie httpOnly
    // Il sera envoyé automatiquement avec credentials: 'include'
    const response = await fetch(`${API_BASE_URL}/twitch/apps/toggle`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage =
        errorData.error || 'Erreur lors du changement de statut';
      log(`Erreur lors du changement de statut: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    log(
      `Statut de l'application changé avec succès: isActive=${result.app?.isActive}`
    );
    return result.app;
  }
}

export default TwitchAppService;
